import { computed, ref, defineAsyncComponent } from 'vue'
import { defineStore } from 'pinia'

// Define the Modal enum based on the file names in the components/modals directory
export enum Modal {
  BaseResumeModal = 'BaseResumeModal',
  ScanUploaderModal = 'ScanUploaderModal',
  OpportunityDetailsModal = 'OpportunityDetailsModal',
  PremiumGateModal = 'PremiumGateModal',
  ResumeFromLinkedinModal = 'ResumeFromLinkedinModal',
  ConfirmationModal = 'ConfirmationModal',
  LinkedinOptimizationTooltipModal = 'LinkedinOptimizationTooltipModal',
  ReportUploadRescanModal = 'ReportUploadRescanModal',
  PaletteModal = 'PaletteModal',
  LinkedInUpgradeModal = 'LinkedInUpgradeModal',
  UpdateJobDescriptionEducationModal = 'UpdateJobDescriptionEducationModal',
  PowerEditUpgradeModal = 'PowerEditUpgradeModal',
  OneClickOptimizerUpgradeModal = 'OneClickOptimizerUpgradeModal',
  ResumeScanUpgradeModal = 'ResumeScanUpgradeModal',
  NetworkMessageGeneratorUpgradeModal = 'NetworkMessageGeneratorUpgradeModal',
  ThankYouNoteModal = 'ThankYouNoteModal',
  ResumeBuilderUpgradeModal = 'ResumeBuilderUpgradeModal',
  ResumeScanAtsTipModal = 'ResumeScanAtsTipModal',
  PendingSuggestionsBlockModal = 'PendingSuggestionsBlockModal',
  CompletedOptimizingModal = 'CompletedOptimizingModal',
  ReportSkillModal = 'ReportSkillModal',
  ResumeOptimizerIntroModal = 'ResumeOptimizerIntroModal',
  PowerEditIntroModal = 'PowerEditIntroModal',
  DownloadedOptimizedResumeModal = 'DownloadedOptimizedResumeModal',
}

export interface ModalContext {
  [key: string]: any
  onClose?: () => void
}

export interface ModalInstance {
  modalName: Modal
  context: ModalContext
  component: any
}

export interface OpenModalOptions {
  keepOthers?: boolean
}

export const useModalStore = defineStore('modalStore', () => {
  const modals = ref<Map<Modal, ModalInstance>>(new Map())
  const currentModalName = ref<Modal | null>(null)
  const openModal = (
    modalName: Modal,
    context: ModalContext = {},
    options: OpenModalOptions = {}
  ) => {
    const { keepOthers = false } = options
    if (!keepOthers) closeModal()

    const modalInstance: ModalInstance = {
      modalName,
      context,
      component: defineAsyncComponent(() =>
        import(`../../components/modals/${modalName}.vue`)
      )
    }
    modals.value.set(modalName, modalInstance)
    currentModalName.value = modalName
  }
  const closeModal = (modalName?: Modal) => {
    if (modalName) {
      const modal = modals.value.get(modalName)

      if (modal?.context?.onClose) modal.context.onClose()

      modals.value.delete(modalName)
      if (currentModalName.value === modalName) currentModalName.value = null
      return
    }

    modals.value.forEach((modal, name) => {
      if (modal.context?.onClose) modal.context.onClose()
      modals.value.delete(name)
    })
    currentModalName.value = null
  }
  return {
    currentModalName,
    modals,
    openModal,
    closeModal,
  }
})
